import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useCapabilitiesQuery } from '@/domains/shared/api/queries/useCapabilitiesQuery';

type Capability = {
  allowed: boolean;
  reason?: string;
};

type ContributionsCapabilities = {
  allowed: boolean;
  reason?: 'AGE' | 'TAX_INFO' | 'PLAN_BLOCKED';
};

type Capabilities = {
  featureFlags: string[];
  beehive: Capability;
  withdraw: Capability;
  plan: Capability;
  tracker: Capability;
  pensionAnalytics: Capability;
  transactionHistory: Capability;
  contributions: ContributionsCapabilities;
};

type CapabilitiesContextValue = {
  capabilities: Capabilities | null;
  redirectRoute: string | null;
  beehiveAccessNotAllowed: boolean;
  contributionsAccessNotAllowed: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

const CapabilitiesContext = createContext<CapabilitiesContextValue | undefined>(
  undefined,
);

export const CapabilitiesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data, isFetching, error, refetch } = useCapabilitiesQuery();
  const capabilities = data?.capabilities ?? null;
  const isLoading = isFetching || (!data && !error);

  const state = {
    capabilities,
    redirectRoute: evaluateBeehiveAccess(data?.capabilities?.beehive?.reason),
    beehiveAccessNotAllowed: data?.capabilities?.beehive?.allowed === false,
    contributionsAccessNotAllowed:
      data?.capabilities.contributions.allowed === false,
    isLoading,
    error: error,
    refetch: async () => {
      await refetch();
    },
  };

  return (
    <CapabilitiesContext.Provider value={{ ...state }}>
      {children}
    </CapabilitiesContext.Provider>
  );
};

export const useCapabilities = (options?: {
  waitFor?: (capabilities: Capabilities | null) => boolean;
  maxAttempts?: number;
}) => {
  const ctx = useContext(CapabilitiesContext);
  if (!ctx) {
    throw new Error('useCapabilities must be used within CapabilitiesProvider');
  }

  const { capabilities, isLoading, refetch } = ctx;
  const pollInterval = useRef<NodeJS.Timeout | null>(null);
  const pollingAttempts = useRef(0);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const maxAttempts = options?.maxAttempts ?? 60; // 60 attempts in 1 minute
  const shouldWait = options?.waitFor ? !options.waitFor(capabilities) : false;

  useEffect(() => {
    const clearPollInterval = () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
        pollInterval.current = null;
      }
    };

    if (!shouldWait) {
      pollingAttempts.current = 0;
      return;
    }

    if (isLoading || pollingTimedOut) return;

    pollInterval.current = setInterval(async () => {
      pollingAttempts.current += 1;
      if (pollingAttempts.current >= maxAttempts) {
        setPollingTimedOut(true);
        clearPollInterval();
      } else {
        await refetch();
      }
    }, 1000);

    return () => clearPollInterval();
  }, [shouldWait, isLoading, pollingTimedOut, refetch, maxAttempts]);

  return {
    ...ctx,
    isLoading: isLoading || (shouldWait && !pollingTimedOut),
    pollingTimedOut,
  };
};

const evaluateBeehiveAccess = (reason?: string | undefined): string | null => {
  if (!reason) return '/hive/beehive/summary';
  switch (reason) {
    case 'IPW':
      return '/hive/investment-pathways/intro';
    case 'MFA_REQUIRED':
      return '/hive/accounts/2fa/intro';
    case 'KYC':
      return '/hive/identity-verification';
    // FIXME: Add this in once we have bulk plan switch in progress and add a page in for it
    // case 'BULK_PLAN_SWITCH_IN_PROGRESS':
    //   return '/hive/bulk-plan-switch-in-progress';
    default:
      return null;
  }
};
